import React from "react";
import { useNavigate } from "react-router-dom";
import { Play, ArrowUpRight, Lock } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { avatarSrc } from "@/lib/defaults";
import { useApp } from "@/context/AppContext";
import { BeforeAfterAuto } from "@/components/post/BeforeAfter";

// Carte d'une publication partagée dans une story (« Ajouter à ma story ») : auteur, extrait, photo / affiche
// vidéo, verset. `compact` = vignette de la rangée des stories ; `interactive` = un tap ouvre la publication.
export const SharedPostCard = ({ sp, compact = false, interactive = true, testId = "shared-post-card" }) => {
  const { t } = useApp();
  const nav = useNavigate();
  if (!sp) return null;
  const author = sp.author || {};
  const open = (e) => { e.stopPropagation(); nav(`/post/${sp.id}`); };
  const Tag = interactive ? "button" : "div";
  if (sp.private) {
    // Publication d'un profil privé non suivi : la carte ne révèle rien (ni texte ni média).
    return (
      <div className={`w-full rounded-xl overflow-hidden text-left ${compact ? "p-2" : "p-4"}`} style={{ background: "rgba(255,255,255,0.92)", color: "#1f2937" }} data-testid={`${testId}-private`}>
        <div className="flex items-center gap-2">
          <Lock size={compact ? 12 : 16} className="shrink-0" style={{ color: "#6b7280" }} />
          <div className="min-w-0">
            <div className={`font-semibold ${compact ? "text-[9px]" : "text-sm"}`}>{t("story.shared_private", "Contenu privé")}</div>
            {!compact && <div className="text-xs mt-0.5" style={{ color: "#6b7280" }}>{t("story.shared_private_hint", "Publication d'un compte privé — réservée à ses abonnés.")}</div>}
          </div>
        </div>
      </div>
    );
  }
  const img = sp.image ? resolveMediaUrl(sp.image) : null;
  const ba = sp.before_after && Array.isArray(sp.before_after.items) && sp.before_after.items.length === 2 ? sp.before_after : null;
  if (compact) {
    return (
      <div className="relative w-full rounded-md overflow-hidden text-left" style={{ background: "rgba(255,255,255,0.95)", color: "#1f2937" }} data-testid={testId}>
        {ba ? <BeforeAfterAuto items={ba.items} kind={ba.kind} compact testId={`${testId}-before-after`} className="aspect-[4/3]" style={{ aspectRatio: "4 / 3" }} />
          : img && <img src={img} alt="" loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover" />}
        <div className="px-1 py-0.5">
          <div className="text-[6px] font-bold truncate">{author.name}</div>
          {sp.text && <div className="text-[6px] leading-tight line-clamp-2">{sp.text}</div>}
        </div>
      </div>
    );
  }
  return (
    <Tag
      type={interactive ? "button" : undefined}
      onClick={interactive ? open : undefined}
      className="block w-full text-left rounded-2xl overflow-hidden"
      style={{ background: "rgba(255,255,255,0.97)", color: "#1f2937", boxShadow: "0 14px 44px rgba(0,0,0,0.35)" }}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 px-3 pt-3 pb-2">
        <Avatar className="h-8 w-8">
          <AvatarImage src={avatarSrc(author, 32)} />
          <AvatarFallback>{author.name?.[0]}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 text-sm font-semibold truncate" data-testid={`${testId}-author`}>
            <span className="truncate">{author.name}</span>
            {(author.is_verified || author.is_church) && <VerifiedBadge user={author} size={13} interactive={false} />}
          </div>
          <div className="text-[10px] uppercase tracking-wide" style={{ color: "#6b7280" }}>Yalola</div>
        </div>
      </div>
      {sp.text && (
        // Le clamp vit dans un <p> sans padding : un padding sur l'élément clampé laisse apparaître la ligne coupée.
        <div className="px-3 pb-2"><p className={`text-sm leading-snug ${img || ba ? "line-clamp-3" : "line-clamp-6"}`} data-testid={`${testId}-text`}>{sp.text}</p></div>
      )}
      {sp.verse?.reference && sp.text && !sp.text.startsWith("«") && (
        <div className="px-3 pb-2 text-xs italic" style={{ color: "#6b7280" }}>— {sp.verse.reference}</div>
      )}
      {ba ? (
        // « Avant / Après » : curseur animé (va-et-vient), vidéos → leurs affiches ; un tap (sans glisser) ouvre la publication.
        <BeforeAfterAuto items={ba.items} kind={ba.kind} testId={`${testId}-before-after`} style={{ maxHeight: "46vh" }} />
      ) : img && (
        <div className="relative">
          <img src={img} alt="" loading="lazy" decoding="async" className="w-full max-h-[46vh] object-cover" data-testid={`${testId}-image`} />
          {sp.has_video && (
            <span className="absolute inset-0 grid place-items-center pointer-events-none">
              <span className="h-12 w-12 rounded-full grid place-items-center text-white" style={{ background: "rgba(0,0,0,0.5)" }} aria-label={t("story.shared_video", "Vidéo")}><Play size={22} fill="#fff" /></span>
            </span>
          )}
        </div>
      )}
      {interactive && (
        <div className="flex items-center justify-between px-3 py-2 text-xs font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid={`${testId}-open`}>
          {t("story.shared_open", "Voir la publication")} <ArrowUpRight size={14} />
        </div>
      )}
    </Tag>
  );
};

export default SharedPostCard;
